import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraDataTable, SpecraMetricStrip } from '@mono/ui-components/captains';
import { SpecraButton, SpecraCheckbox, SpecraSelect } from '@mono/ui-components/ensigns';
import { SpecraFormField, SpecraSectionHeader } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { PricingEngine } from '../../../../../lib/utils/pricingEngine';
import { money } from '../ratesView';
import { ParameterField } from './ParameterField';
const presets = [
    {
        poolName: 'Fringe',
        allocationBase: 'direct_labor',
        appliesToCostTypes: [
            'labor'
        ]
    },
    {
        poolName: 'Overhead',
        allocationBase: 'direct_labor_plus_fringe',
        appliesToCostTypes: [
            'labor'
        ]
    },
    {
        poolName: 'G&A',
        allocationBase: 'total_cost_input',
        appliesToCostTypes: [
            'labor',
            'travel',
            'odc'
        ]
    },
    {
        poolName: 'Facilities',
        allocationBase: 'direct_labor',
        appliesToCostTypes: [
            'labor'
        ]
    },
    {
        poolName: 'Material Handling',
        allocationBase: 'total_cost_input',
        appliesToCostTypes: [
            'material'
        ]
    }
];
export function IndirectsTab({ config, onChange }) {
    const current = config ?? {
        pools: [],
        useMultiplicative: true,
        gaBaseMethod: 'total_cost_input'
    };
    const engine = new PricingEngine();
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                title: "Indirect rates"
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Multiplicative cascading (DCAA standard)",
                htmlFor: "pricing-cascading",
                children: /*#__PURE__*/ _jsx(SpecraCheckbox, {
                    id: "pricing-cascading",
                    "aria-label": "Multiplicative cascading (DCAA standard)",
                    checked: current.useMultiplicative,
                    onCheckedChange: (checked)=>onChange({
                            ...current,
                            useMultiplicative: checked === true
                        })
                })
            }),
            /*#__PURE__*/ _jsx(SpecraDataTable, {
                "data-testid": Testids.pricingWorkspace.indirectsTable,
                rows: current.pools,
                rowKey: (pool)=>pool.poolName,
                getRowLabel: (pool)=>pool.poolName,
                columns: [
                    {
                        id: 'name',
                        header: 'Pool',
                        cell: (pool)=>pool.poolName
                    },
                    {
                        id: 'rate',
                        header: 'Rate % (fraction)',
                        cell: (pool)=>/*#__PURE__*/ _jsx(ParameterField, {
                                label: `${pool.poolName} rate (fraction)`,
                                param: {
                                    name: pool.poolName,
                                    label: pool.poolName,
                                    value: pool.ratePercent,
                                    source: 'contractor',
                                    paramType: 'percentage',
                                    perPeriod: false,
                                    constraints: {
                                        min: 0
                                    }
                                },
                                editable: true,
                                onChange: (value)=>onChange({
                                        ...current,
                                        pools: current.pools.map((p)=>p.poolName === pool.poolName ? {
                                                ...p,
                                                ratePercent: value ?? 0
                                            } : p)
                                    })
                            })
                    },
                    {
                        id: 'base',
                        header: 'Allocation base',
                        cell: (pool)=>pool.allocationBase.replace(/_/g, ' ')
                    },
                    {
                        id: 'cost',
                        header: 'Applies to',
                        cell: (pool)=>pool.appliesToCostTypes.join(', ')
                    },
                    {
                        id: 'actions',
                        header: 'Actions',
                        cell: (pool)=>/*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "ghost",
                                size: "xs",
                                "aria-label": `Remove ${pool.poolName}`,
                                onClick: ()=>onChange({
                                        ...current,
                                        pools: current.pools.filter((p)=>p.poolName !== pool.poolName).map((p, i)=>({
                                                ...p,
                                                applyOrder: i + 1
                                            }))
                                    }),
                                children: "Remove"
                            })
                    }
                ]
            }),
            /*#__PURE__*/ _jsxs(SpecraSelect, {
                "aria-label": "Add indirect pool",
                value: "",
                onChange: (event)=>{
                    const preset = presets.find((p)=>p.poolName === event.target.value);
                    if (preset) onChange({
                        ...current,
                        pools: [
                            ...current.pools,
                            {
                                ...preset,
                                applyOrder: current.pools.length + 1,
                                ratePercent: 0
                            }
                        ]
                    });
                },
                children: [
                    /*#__PURE__*/ _jsx("option", {
                        value: "",
                        children: "Add pool…"
                    }),
                    presets.filter((p)=>!current.pools.some((pool)=>p.poolName === pool.poolName)).map((p)=>/*#__PURE__*/ _jsx("option", {
                            children: p.poolName
                        }, p.poolName))
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraMetricStrip, {
                density: "compact",
                metrics: [
                    {
                        id: 'wrap',
                        label: 'Labor wrap rate',
                        value: `${engine.computeWrapRateFromConfig(current, 'labor').toFixed(4)}×`,
                        tone: 'neutral'
                    },
                    ...engine.computeIndirectBreakdown(current, 100000, 'labor').map((p)=>({
                            id: p.poolName,
                            label: `${p.poolName} on $100k direct labor`,
                            value: money(String(p.amount)),
                            tone: 'neutral'
                        }))
                ]
            })
        ]
    });
}
